import { inject, Injectable } from '@angular/core'
import { MinTradingViewDate } from '@modules/trading-view-module/constants/min-trading-view-date'
import { MinutesTimeframes } from '@modules/trading-view-module/constants/minutes-timeframes'
import { ResolutionMap } from '@modules/trading-view-module/constants/resolution-map'
import { TradingViewTimeframesEnum } from '@modules/trading-view-module/enums/trading-view-timeframes.enum'
import { FromToInterface } from '@modules/trading-view-module/interfaces/from-to.interface'
import { ResolutionMapValueInterface } from '@modules/trading-view-module/interfaces/resolution-map-value.interface'
import { ResolutionMapType } from '@modules/trading-view-module/types/resolution-map.type'
import {
	Bar,
	PeriodParams,
	ResolutionString,
	SubscribeBarsCallback,
	TimeFrameValue,
} from '@public/trading-view/charting_library'
import { MinutesInHour } from '@shared/constants/times/minutes-in-hour'
import { MsInHour } from '@shared/constants/times/ms-in-hour'
import { MsInMinute } from '@shared/constants/times/ms-in-minute'
import { MsInSecond } from '@shared/constants/times/ms-in-second'
import { BehaviorSubject, map, Observable } from 'rxjs'
import { StocksService } from '@modules/stocks-module/general/services/stocks.service'
import { LatestPricesResponseInterface } from '@modules/stocks-module/general/interfaces/latest-prices-response.interface'
import { LatestPricesInterface } from '@modules/stocks-module/general/interfaces/latest-prices.interface'
import { SocketStockAInterface } from '@modules/prices-module/socket-prices/interfaces/socket-stock-a.interface'
import { SocketStockTInterface } from '@modules/prices-module/socket-prices/interfaces/socket-stock-t.interface'

@Injectable({
	providedIn: 'root',
})
export class TradingViewHelperService {
	protected readonly math: Math = Math
	protected readonly date: DateConstructor = Date
	protected readonly msInSecond: number = MsInSecond
	protected readonly msInMinute: number = MsInMinute
	protected readonly msInHour: number = MsInHour
	protected readonly minutesInHour: number = MinutesInHour
	protected readonly minTradingViewDate: string = MinTradingViewDate
	protected readonly resolutionMap: ResolutionMapType = ResolutionMap
	protected readonly tradingViewTimeframesEnum: typeof TradingViewTimeframesEnum = TradingViewTimeframesEnum
	protected readonly minutesTimeframes: TradingViewTimeframesEnum[] = MinutesTimeframes

	public tradingViewState$: BehaviorSubject<object | null> = new BehaviorSubject<object | null>(null)
	public tradingViewTimeframe$: BehaviorSubject<TradingViewTimeframesEnum> =
		new BehaviorSubject<TradingViewTimeframesEnum>(this.tradingViewTimeframesEnum.oneDay)
	public tradingViewPeriod$: BehaviorSubject<TimeFrameValue | null> = new BehaviorSubject<TimeFrameValue | null>(null)

	public lastBars: Map<string, Bar> = new Map()

	private readonly _stocksService: StocksService = inject<StocksService>(StocksService)

	public setTradingViewState(state: object | null): void {
		this.tradingViewState$.next(state)
	}

	public setTradingViewTimeframe(timeframe: TradingViewTimeframesEnum): void {
		this.tradingViewTimeframe$.next(timeframe)
	}

	public setTradingViewPeriod(period: TimeFrameValue | undefined): void {
		this.tradingViewPeriod$.next(period ?? null)
	}

	public generateUrlBars(resolution: TradingViewTimeframesEnum, from: number, to: number): string {
		const resolutionInfo: ResolutionMapValueInterface = this.resolutionMap[resolution]
		return `${resolutionInfo.multiplier}/${resolutionInfo.timespan}/${from}/${to}`
	}

	public getNormalizedDates(resolution: TradingViewTimeframesEnum, periodParams: PeriodParams): FromToInterface {
		const minDate: number = new this.date(this.minTradingViewDate).getTime()
		let fromUnix: number = minDate
		let toUnix: number = this.math.max(periodParams.to * this.msInSecond, minDate)

		if (this.minutesTimeframes.includes(resolution)) {
			const minutes: number = parseInt(resolution)
			const roundDownToNearestMinute: CallableFunction = (timestamp: number, minutes: number): number =>
				this.math.floor(timestamp / (minutes * this.msInMinute)) * (minutes * this.msInMinute)

			fromUnix = roundDownToNearestMinute(fromUnix, minutes)
			toUnix = roundDownToNearestMinute(toUnix, minutes)
		}

		return { fromUnix, toUnix }
	}

	public getLastBarDay(symbol: string): Observable<Bar | null> {
		const now: Date = new this.date()
		const utcMinusFiveHours: Date = new this.date(now.getTime() - 5 * this.msInHour)
		const lastBarDate: Date = new this.date(
			utcMinusFiveHours.getUTCFullYear(),
			utcMinusFiveHours.getUTCMonth(),
			utcMinusFiveHours.getUTCDate(),
		)
		const plusFiveSeconds: Date = new this.date(lastBarDate.getTime() + 5000)

		const resolutionString: string = `1/second/${lastBarDate.getTime()}/${plusFiveSeconds.getTime()}`

		return this._stocksService.getTickerHistory(symbol, resolutionString).pipe(
			map((response: LatestPricesResponseInterface): Bar | null => {
				const results: LatestPricesInterface[] = response.results || []

				if (results.length === 0) return null

				const bars: Bar[] = results.reverse().map(
					(item: LatestPricesInterface): Bar => ({
						time: item.t,
						open: item.o,
						high: item.h,
						low: item.l,
						close: item.c,
						volume: item.v,
					}),
				)

				return bars.slice(-1)[0] ?? null
			}),
		)
	}

	public getTimeLastBar(resolution: ResolutionString, lastBarTime?: number | undefined): number {
		const now: Date = new this.date()
		now.setUTCMilliseconds(0)

		let barTime: number

		switch (resolution) {
			case this.tradingViewTimeframesEnum.oneMinute:
			case this.tradingViewTimeframesEnum.threeMinutes:
			case this.tradingViewTimeframesEnum.fiveMinutes:
			case this.tradingViewTimeframesEnum.fifteenMinutes:
			case this.tradingViewTimeframesEnum.thirtyMinutes: {
				const timeframe: number = parseInt(resolution, 10)
				const msInTimeframe: number = timeframe * this.msInMinute

				if (lastBarTime) {
					const msPassed: number = now.getTime() - lastBarTime

					if (msPassed >= msInTimeframe) {
						barTime = lastBarTime + msInTimeframe
					} else {
						barTime = lastBarTime
					}
				} else {
					const minutes: number = this.math.floor(now.getUTCMinutes() / timeframe) * timeframe
					now.setUTCMinutes(minutes, 0, 0)
					barTime = now.getTime()
				}
				break
			}

			case this.tradingViewTimeframesEnum.oneHour:
			case this.tradingViewTimeframesEnum.twoHours:
			case this.tradingViewTimeframesEnum.threeHours:
			case this.tradingViewTimeframesEnum.fourHours: {
				const timeframeHours: number = parseInt(resolution) / this.minutesInHour
				const msInTimeframe: number = timeframeHours * this.msInHour

				if (lastBarTime) {
					const msPassed: number = now.getTime() - lastBarTime
					if (msPassed >= msInTimeframe) {
						barTime = lastBarTime + msInTimeframe
					} else {
						barTime = lastBarTime
					}
				} else {
					const hours: number = this.math.floor(now.getUTCHours() / timeframeHours) * timeframeHours
					now.setUTCHours(hours, 0, 0, 0)
					barTime = now.getTime()
				}
				break
			}

			case this.tradingViewTimeframesEnum.oneDay: {
				now.setUTCHours(0, 0, 0, 0)
				barTime = now.getTime()

				if (lastBarTime && barTime < lastBarTime) {
					barTime = lastBarTime
				}

				break
			}

			case this.tradingViewTimeframesEnum.oneWeek: {
				const day: number = now.getUTCDay()
				const diff: number = day === 0 ? -6 : 1 - day

				now.setUTCDate(now.getUTCDate() + diff)
				now.setUTCHours(0, 0, 0, 0)
				barTime = now.getTime()

				if (lastBarTime && barTime < lastBarTime) {
					barTime = lastBarTime
				}

				break
			}

			case this.tradingViewTimeframesEnum.oneMonth: {
				now.setUTCDate(1)
				now.setUTCHours(0, 0, 0, 0)
				barTime = now.getTime()

				if (lastBarTime && barTime < lastBarTime) {
					barTime = lastBarTime
				}

				break
			}

			case this.tradingViewTimeframesEnum.oneYear: {
				now.setUTCMonth(0, 1)
				now.setUTCHours(0, 0, 0, 0)
				barTime = now.getTime()

				if (lastBarTime && barTime < lastBarTime) {
					barTime = lastBarTime
				}

				break
			}

			default: {
				now.setUTCSeconds(0, 0)
				barTime = now.getTime()

				if (lastBarTime && barTime < lastBarTime) {
					barTime = lastBarTime
				}

				break
			}
		}

		if (lastBarTime && barTime < lastBarTime) {
			barTime = lastBarTime
		}

		return barTime
	}

	public buildRealtimeBar(
		barKey: string,
		lastBarTime: number,
		stockA: SocketStockAInterface | undefined,
		stockT: SocketStockTInterface | undefined,
		onTick: SubscribeBarsCallback,
	): void {
		const lastBar: Bar | undefined = this.lastBars.get(barKey)
		const isNewBar: boolean = !lastBar || lastBar.time !== lastBarTime

		let open: number = 0
		let high: number = 0
		let low: number = 0
		let close: number = 0
		let volume: number = 0

		if (isNewBar) {
			open = stockA?.o ?? lastBar?.close ?? 0
			close = open
			high = open
			low = open
			volume = 0
		} else {
			open = lastBar?.open ?? 0
			close = lastBar?.close ?? 0
			high = lastBar?.high ?? 0
			low = lastBar?.low ?? 0
			volume = lastBar?.volume ?? 0
		}

		if (stockT) {
			close = stockT.p
			if (stockT.p > high) high = stockT.p
			if (stockT.p < low) low = stockT.p
			volume += stockT.s
		}

		if (stockA) {
			if (stockA.h > high) high = stockA.h
			if (stockA.l < low) low = stockA.l
			if (!stockT?.p) close = stockA.c
			volume += stockA.v
		}

		const newBar: Bar = { time: lastBarTime, open, close, high, low, volume }

		this.lastBars.set(barKey, newBar)
		onTick(newBar)
	}
}
